import { Button } from "@/components";
import { PublicRoutes } from "@/constants/routes";
import { Select } from "@/design-system";
import { useToast } from "@/hooks";
import {
  EMPTY_DEMO_LEAD,
  STORES_OPTIONS,
  demoLeadSchema,
  type DemoLeadValues,
} from "@/pages/landingPage/constants/demoLead";
import { useSendDemoLead } from "@/pages/landingPage/hooks/useSendDemoLead";
import { phoneUtils } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

const FIELD_LABEL = "text-sm font-semibold text-ds-ink-900";
const FIELD_BOX =
  "h-12 w-full rounded-xl border border-ds-ink-300 bg-ds-surface px-4 text-base text-ds-ink-900 shadow-sm outline-none transition-colors placeholder:text-ds-ink-500 focus:border-ds-brand-500";

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <span className="text-ds-danger-700 text-xs">{message}</span>
  ) : null;

/**
 * Form da LP de régua. Pede o mesmo que o modal "Agende sua demo" e segue o
 * mesmo envio (/api/lead → e-mail, Discord e CRM; depois /boas-vindas), mas
 * com todos os campos visíveis de uma vez.
 */
export const ReguaLeadForm = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const sendDemoLead = useSendDemoLead("regua-cobranca");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, formState, register } =
    useForm<DemoLeadValues>({
      resolver: zodResolver(demoLeadSchema),
      mode: "onChange",
      defaultValues: EMPTY_DEMO_LEAD,
    });

  const onSubmit = async (data: DemoLeadValues) => {
    setIsSubmitting(true);
    try {
      await sendDemoLead(data);
      navigate(PublicRoutes.BOAS_VINDAS);
    } catch (err) {
      console.error("[regua-cobranca] submit failed", err);
      showToast(
        "Não conseguimos enviar agora. Tente novamente ou fale direto pelo WhatsApp.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-5"
      noValidate
    >
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className={FIELD_LABEL}>
              Nome
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Como podemos te chamar?"
              className={FIELD_BOX}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        control={control}
        name="phone"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label htmlFor="phone" className={FIELD_LABEL}>
              WhatsApp
            </label>
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="(11) 99999-9999"
              className={FIELD_BOX}
              value={field.value ?? ""}
              onChange={e =>
                field.onChange(phoneUtils.maskPhone(e.target.value))
              }
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        control={control}
        name="company"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label htmlFor="company" className={FIELD_LABEL}>
              Nome da rede
            </label>
            <input
              id="company"
              type="text"
              autoComplete="organization"
              placeholder="Ex.: Lojas Modelo"
              className={FIELD_BOX}
              value={field.value ?? ""}
              onChange={field.onChange}
            />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        control={control}
        name="storesRange"
        render={({ field, fieldState }) => (
          <Select
            name="storesRange"
            label="Quantas lojas a rede tem?"
            placeholder="Selecione..."
            options={STORES_OPTIONS}
            value={field.value ?? ""}
            onValueChange={field.onChange}
            error={fieldState.error?.message}
            required
          />
        )}
      />

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...register("honeypot")}
      />

      <Button
        type="submit"
        disabled={!formState.isValid || isSubmitting}
        isLoading={isSubmitting}
        className="disabled:bg-ds-ink-200 disabled:text-ds-ink-500 mt-3 h-14 w-full rounded-full text-base font-bold"
      >
        FALAR COM UM ESPECIALISTA
      </Button>
    </form>
  );
};
